import { z } from "zod";

export const FoqaPhaseSchema = z.enum([
	"general",
	"departure",
	"arrival",
	"approach_landing",
]);

export type FoqaPhase = z.infer<typeof FoqaPhaseSchema>;

export const FoqaBandSpecSchema = z.object({
	green: z.tuple([z.number(), z.number()]),
	yellow: z.array(z.tuple([z.number(), z.number()])).optional(),
	relativeTo: z.enum(["vref"]).optional(),
	target: z.number().optional(),
	gradedInGreen: z.boolean().optional(),
	penalty: z.object({
		kind: z.enum(["quadraticAbs", "linearRel", "quadraticRel"]),
		k: z.number(),
	}).optional(),
});

export type FoqaBandSpec = z.infer<typeof FoqaBandSpecSchema>;

export const FoqaSafetyEventSegmentSchema = z.enum(["entire_flight", "approach"]);
export type FoqaSafetyEventSegment = z.infer<typeof FoqaSafetyEventSegmentSchema>;

export const FoqaSafetyEventDirSchema = z.enum(["above", "below"]);
export type FoqaSafetyEventDir = z.infer<typeof FoqaSafetyEventDirSchema>;

export const FoqaSafetyEventSpecSchema = z.object({
	min: z.number().optional(),
	max: z.number().optional(),
	/** vmo/mmo read the model limit pinned on the debrief (`measurements.limits`). */
	relativeTo: z.enum(["vref", "vmo", "mmo"]).optional(),
	segment: FoqaSafetyEventSegmentSchema.optional(),
	belowAfe: z.number().optional(),
	durationSec: z.number().optional(),
	entity: z.string().optional(),
	dir: FoqaSafetyEventDirSchema.optional(),
	caution: z.number().optional(),
	warning: z.number().optional(),
});

export type FoqaSafetyEventSpec = z.infer<typeof FoqaSafetyEventSpecSchema>;

export const FoqaFlagSpecSchema = z.object({
	key: z.string(),
	label: z.string(),
	phase: FoqaPhaseSchema,
	entity: z.string(),
	appliesTo: z.enum(["visual", "instrument", "both"]).optional(),
	segment: FoqaSafetyEventSegmentSchema.optional(),
	belowAfe: z.number().optional(),
	durationSec: z.number().optional(),
	/** vmo/mmo read the model limit pinned on the debrief (`measurements.limits`). */
	relativeTo: z.enum(["vref", "vmo", "mmo"]).optional(),
	dir: FoqaSafetyEventDirSchema.optional(),
	min: z.number().optional(),
	max: z.number().optional(),
	caution: z.number().optional(),
	warning: z.number().optional(),
	enabled: z.boolean().optional(),
});
export type FoqaFlagSpec = z.infer<typeof FoqaFlagSpecSchema>;

export const FoqaFlagConfigSchema = z.array(FoqaFlagSpecSchema);
export type FoqaFlagConfig = z.infer<typeof FoqaFlagConfigSchema>;

export const FoqaFlagEventSchema = z.object({
	key: z.string(),
	label: z.string(),
	phase: FoqaPhaseSchema,
	tier: z.enum(["caution", "warning"]),
	actual: z.number().nullable(),
});
export type FoqaFlagEvent = z.infer<typeof FoqaFlagEventSchema>;
export type FoqaDebriefFlags = FoqaFlagEvent[];

export const FoqaGateSchema = z.enum(["gate_1000", "gate_500", "gate_200"]);
export type FoqaGate = z.infer<typeof FoqaGateSchema>;

export const FOQA_GATE_LABEL: Record<FoqaGate, string> = {
	gate_1000: "1,000 ft — configured",
	gate_500: "500 ft — converging",
	gate_200: "200 ft — stable or go around",
};

/** Descent order, which is the order every surface shows the gates in. */
export const FOQA_GATE_ORDER: readonly FoqaGate[] = ["gate_1000", "gate_500", "gate_200"];

/** From the key, not the config: stored summary rows persist keys, so every surface can group a
 *  debrief without loading its threshold row alongside. */
export const foqaGateForKey = (key: string): FoqaGate | null => {
	const gate = key.split("_").slice(0, 2).join("_");
	return (FOQA_GATE_ORDER as readonly string[]).includes(gate) ? gate as FoqaGate : null;
};

export const FoqaThresholdParamSchema = z.object({
	key: z.string(),
	label: z.string(),
	phase: FoqaPhaseSchema,
	/** Stability gate this parameter is read at. Absent = not a gate reading. */
	gate: FoqaGateSchema.optional(),
	unit: z.string(),
	weight: z.number(),
	target: z.string().optional(),
	green: z.string().optional(),
	yellow: z.string().optional(),
	red: z.string().optional(),
	dataSource: z.string().optional(),
	bands: FoqaBandSpecSchema.optional(),
	safetyEvent: FoqaSafetyEventSpecSchema.optional(),
	appliesTo: z.enum(["visual", "instrument", "both"]).optional(),
	enabled: z.boolean().optional(),
	advisoryOnly: z.boolean().optional(),
	/** Measure and report, never grade: the value earns trust before it can move a score. */
	informational: z.boolean().optional(),
	segment: FoqaSafetyEventSegmentSchema.optional(),
	belowAfe: z.number().optional(),
	durationSec: z.number().optional(),
	entity: z.string().optional(),
});

export const FoqaTplPolicySchema = z.object({
	/** Operator safety factor on the AFM distance; CJP SEF elected 25% over the FAA's 15% minimum. */
	factor: z.number().positive().optional(),
	/** Assumed 50 ft-to-touchdown air distance added back when siting the limit. */
	airDistanceFt: z.number().positive().optional(),
	/**
	 * Whether a computed TPL suppresses `runway_remaining_at_touchdown`. Off while TPL is being
	 * validated against reviewer judgement — seeing both is how the limit earns trust against the
	 * fixed floor. Both are advisory (weight 0), so showing both cannot move a score.
	 */
	supersedesRunwayRemaining: z.boolean().optional(),
});

export type FoqaTplPolicy = z.infer<typeof FoqaTplPolicySchema>;

/**
 * Approach type is classified from the weather, not the track. Most approaches are flown
 * visually, and grading a visual approach against instrument procedures invents deviations from
 * a procedure nobody was flying.
 */
export const FoqaVisualApproachPolicySchema = z.object({
	/** Below this ceiling the approach is instrument. Default 2000 ft. */
	ceilingMinFt: z.number().positive().optional(),
	/** Below this visibility the approach is instrument. Default 5 SM. */
	visibilityMinSm: z.number().positive().optional(),
	/** Kill switch — off restores the track-geometry classification without a deploy. */
	enabled: z.boolean().optional(),
});

export type FoqaVisualApproachPolicy = z.infer<typeof FoqaVisualApproachPolicySchema>;

/** Landing from the go-around band at 200 ft caps the total: a weighted average cannot say "you
 *  should have gone around" — ten green readings dilute one red. */
export const FoqaUnstableApproachPolicySchema = z.object({
	/** Ceiling when a 200 ft reading reached the go-around band. */
	capScore: z.number().min(0).max(100).optional(),
	/** Ceiling for a caution at 200 ft. Omit to leave cautions to their own weights. */
	cautionCapScore: z.number().min(0).max(100).optional(),
	/** Kill switch — off leaves the 200 ft readings to their own weights. */
	enabled: z.boolean().optional(),
});

export type FoqaUnstableApproachPolicy = z.infer<typeof FoqaUnstableApproachPolicySchema>;

export const FoqaScoringPolicySchema = z.object({
	shallowSinkAdvisory: z.boolean().optional(),
	unstableApproach: FoqaUnstableApproachPolicySchema.optional(),
	tpl: FoqaTplPolicySchema.optional(),
	visualApproach: FoqaVisualApproachPolicySchema.optional(),
});

export type FoqaScoringPolicy = z.infer<typeof FoqaScoringPolicySchema>;

export const FoqaApproachTypeSchema = z.enum(["visual", "instrument"]);
export type FoqaApproachType = z.infer<typeof FoqaApproachTypeSchema>;

export const DEFAULT_FOQA_SCORING_POLICY: FoqaScoringPolicy = {
	shallowSinkAdvisory: true,
	// Red only: over 40 line flights a third had a reading just outside green at 200 and none were
	// ever red, so a green-edge ceiling would call a third of ordinary landings unstable.
	unstableApproach: { capScore: 69, enabled: true },
};

/** Derived from the gate's own readings, so it cannot disagree with them. Not a config param. */
export const FOQA_STABLE_AT_200_KEY = "gate_200_stable";

export type FoqaThresholdParam = z.infer<typeof FoqaThresholdParamSchema>;

export const FoqaThresholdConfigSchema = z.array(FoqaThresholdParamSchema);

export type FoqaThresholdConfig = z.infer<typeof FoqaThresholdConfigSchema>;

/** The universal fallthrough profile — scores any model without a row of its own. */
export const FOQA_DEFAULT_PROFILE_KEY = "default";

/**
 * Offered as suggestions when staff creates a profile — NOT a whitelist: resolution falls
 * back to the raw lowercased `aircraft.model`, so a tail whose model is "Sandbox"
 * legitimately resolves a "sandbox" profile.
 */
export { FOQA_SEEDED_PROFILE_KEYS as FOQA_SUGGESTED_PROFILE_KEYS } from "./foqa-vref";

/** Match how scoring derives a model key, so a created profile can actually be resolved. */
export const normalizeProfileKey = (key: string): string => key.trim().toLowerCase();

/**
 * Out-of-goal decay is a GLOBAL setting: cohorts tune where the bands sit, staff owns how
 * hard a miss is punished. Cohort rows are persisted without `bands.penalty`
 * (see stripPenalties) and get the global curve merged back in at resolve time, so a staff
 * edit reaches every cohort without rewriting their rows.
 */
export const withGlobalPenalties = (
	config: FoqaThresholdConfig,
	globalConfig: FoqaThresholdConfig,
): FoqaThresholdConfig => {
	const byKey = new Map(globalConfig.map((p) => [p.key, p.bands?.penalty]));
	return config.map((p) => {
		if (!p.bands) {
			return p;
		}
		const penalty = byKey.get(p.key);
		const bands = { ...p.bands };
		if (penalty) {
			bands.penalty = { ...penalty };
		} else {
			delete bands.penalty;
		}
		return { ...p, bands };
	});
};

/** Drop the globally-owned decay curve before persisting a cohort-scoped config. */
export const stripPenalties = (config: FoqaThresholdConfig): FoqaThresholdConfig =>
	config.map((p) => {
		if (!p.bands?.penalty) {
			return p;
		}
		const bands = { ...p.bands };
		delete bands.penalty;
		return { ...p, bands };
	});

export const FoqaBandSchema = z.enum(["green", "yellow", "red", "na"]);

export type FoqaBand = z.infer<typeof FoqaBandSchema>;

export const FoqaResultPresentationSchema = z.enum(["scored", "informational", "unavailable"]);

export type FoqaResultPresentation = z.infer<typeof FoqaResultPresentationSchema>;

export const FoqaDebriefParamResultSchema = z.object({
	key: z.string(),
	label: z.string(),
	phase: z.string(),
	actual: z.number().nullable(),
	actualText: z.string().optional(),
	score: z.number(),
	weight: z.number(),
	band: FoqaBandSchema,
	/** Whether this param tripped its curated safety-event gate (WARNING/hard tier). */
	safetyEvent: z.boolean().optional(),
	cautionEvent: z.boolean().optional(),
	/** Absent on every row written before US-002 — see `foqaResultPresentation`. */
	presentation: FoqaResultPresentationSchema.optional(),
	/** Why nothing was measured, for the surfaces that say so instead of hiding the row. */
	unavailableReason: z.string().optional(),
	/** The param cannot apply to this flight at all (wrong approach type) — surfaces omit the
	 *  row entirely rather than graying it: an ILS row on a visual approach is noise, not a gap. */
	notApplicable: z.boolean().optional(),
});

export type FoqaDebriefParamResult = z.infer<typeof FoqaDebriefParamResultSchema>;

/** Scorecard rows written before `notApplicable` existed carry only the label. */
export const FOQA_NOT_APPLICABLE_REASON = "Not applicable to this approach type";

/**
 * Whether a row should be omitted from every surface: inapplicable to the flight, as opposed
 * to unmeasured — "Gear channel unavailable" is a gap worth showing, an ILS row on a visual
 * approach is not. The string check covers rows persisted before the flag existed.
 */
export const isFoqaTopicHidden = (
	result: { notApplicable?: boolean; unavailableReason?: string },
): boolean => result.notApplicable === true || result.unavailableReason === FOQA_NOT_APPLICABLE_REASON;

/**
 * How a stored row must be read. Legacy rows carry no `presentation`, and for them band "na"
 * was the only way to say "nothing was measured" — so the fallback reproduces exactly what
 * every surface did before the field existed.
 *
 * INFORMATIONAL is measured and shown but never graded: no score, weight, goal or band. It is
 * deliberately NOT expressible as band "na", which the renderers drop as unmeasured.
 */
export const foqaResultPresentation = (
	result: { band: string; presentation?: string | null },
): FoqaResultPresentation => {
	if (result.presentation === "informational" || result.presentation === "unavailable") {
		return result.presentation;
	}
	return result.band === "na" ? "unavailable" : "scored";
};

export const isFoqaInformational = (result: { band: string; presentation?: string | null }): boolean =>
	foqaResultPresentation(result) === "informational";

export const FoqaDebriefSummarySchema = z.array(FoqaDebriefParamResultSchema);

export type FoqaDebriefSummary = z.infer<typeof FoqaDebriefSummarySchema>;

export type FoqaTopicStatus = "active" | "unavailable";

/**
 * A topic's ordering status: ACTIVE covers every measured row, scored or informational alike —
 * that split lives in the presentation kind, not here. UNAVAILABLE covers what was never
 * measured, and what has SINCE been disabled even if an older debrief still carries a stored
 * band for it from when it was live — a reseeded config must not let history read as current.
 */
export const foqaTopicStatus = (
	result: { band: string; presentation?: string | null },
	param: { enabled?: boolean } | null | undefined,
): FoqaTopicStatus => (
	param?.enabled === false || foqaResultPresentation(result) === "unavailable"
		? "unavailable"
		: "active"
);

const FOQA_TOPIC_STATUS_RANK: Record<FoqaTopicStatus, number> = { active: 0, unavailable: 1 };

/** Stable: active topics sort ahead of unavailable ones, and — because `Array#sort` is stable —
 *  every row keeps its position relative to its same-status neighbors, so a deliberately
 *  configured order (gate sequence, curated list) survives untouched within each class. */
export const sortFoqaTopicsByStatus = <T,>(
	rows: readonly T[],
	statusOf: (row: T) => FoqaTopicStatus,
): T[] => [...rows].sort((a, b) => FOQA_TOPIC_STATUS_RANK[statusOf(a)] - FOQA_TOPIC_STATUS_RANK[statusOf(b)]);

export const FoqaOperatingLimitsSchema = z.object({
	vmoKt: z.number().nullable(),
	mmo: z.number().nullable(),
	profileVersion: z.number().nullable(),
});

export type FoqaOperatingLimits = z.infer<typeof FoqaOperatingLimitsSchema>;

export const FoqaExceedanceSeriesPointSchema = z.object({
	tSec: z.number(),
	aglFt: z.number().nullable(),
	bank: z.number().nullable(),
	pitch: z.number().nullable(),
	ias: z.number().nullable(),
	mach: z.number().nullable().optional(),
	gndSpd: z.number().nullable(),
	outsideEdgeFt: z.number().nullable(),
});

export type FoqaExceedanceSeriesPoint = z.infer<typeof FoqaExceedanceSeriesPointSchema>;

/**
 * Everything that went into a Touchdown Point Limit, kept so a debrief can show its working.
 *
 * A pilot-facing audit trail, not an internal dump: a reviewer disputing a TPL needs the AFM
 * distance, the conditions it was looked up at, and the observation those conditions came from.
 * Persisted on the debrief so a later config change cannot silently rewrite the explanation of
 * a score that was already sent.
 */
export const FoqaTplBasisSchema = z.object({
	/** TPL = ldaFt − factoredFt + airDistanceFt. */
	ldaFt: z.number(),
	unfactoredFt: z.number(),
	factoredFt: z.number(),
	factor: z.number(),
	airDistanceFt: z.number(),
	/** Conditions the AFM was looked up at. */
	weightLb: z.number(),
	pressureAltFt: z.number(),
	tempC: z.number().nullable(),
	headwindKt: z.number(),
	runwayCondition: z.enum(["contaminated", "dry", "wet"]),
	landingFlap: z.string().optional(),
	vrefAdditiveKt: z.number().optional(),
	/** Phenom-style temperature correction, when the model publishes one. */
	isaDeviationC: z.number().optional(),
	temperatureFactor: z.number().optional(),
	contaminantDepthIn: z.number().optional(),
	/** Which published tables answered, so a disputed number is traceable to a page. */
	baseTableId: z.string().optional(),
	correctionTableId: z.string().optional(),
	/** The observation the conditions were read from. */
	stationId: z.string().optional(),
	/** ISO string, not a Date: the column is a jsonb cast, never parsed on read, so a Date-typed
	 *  field would be a lie every consumer trips over. */
	observedAt: z.string().optional(),
	altimeterInHg: z.number().optional(),
	windDirectionDeg: z.number().optional(),
	windSpeedKt: z.number().optional(),
	precipitation: z.enum(["heavy", "light", "moderate", "none"]).optional(),
	grooved: z.boolean().optional(),
});

export type FoqaTplBasis = z.infer<typeof FoqaTplBasisSchema>;

/** Inputs needed to recompute TPL when a reviewer corrects Vref. Performance tables and
 *  scoring policy are deliberately resolved live; this snapshot preserves the flight facts. */
export const FoqaTplInputSchema = z.object({
	modelKey: z.string(),
	grossWeightLb: z.number().nullable(),
	met: z.object({
		tempC: z.number().nullable(),
		pressureAltFt: z.number().nullable(),
		headwindKt: z.number().nullable(),
		runwayCondition: z.enum(["contaminated", "dry", "wet"]).nullable(),
		refusals: z.array(z.enum([
			"no_altimeter", "no_metar", "no_temperature", "no_wind", "unknown_precipitation",
		])),
		basis: z.object({
			stationId: z.string().optional(),
			observedAt: z.string().optional(),
			altimeterInHg: z.number().optional(),
			windDirectionDeg: z.number().optional(),
			windSpeedKt: z.number().optional(),
			precipitation: z.enum(["heavy", "light", "moderate", "none"]).optional(),
			grooved: z.boolean().optional(),
		}),
	}),
	landingEnd: z.object({
		ldaFt: z.number().nullable(),
		displacedThresholdFt: z.number().nullable(),
		displacedThresholdSurveyed: z.boolean(),
	}),
	landingFlap: z.string().nullable().optional(),
});

export type FoqaTplInput = z.infer<typeof FoqaTplInputSchema>;

export const FoqaApproachTypeBasisSchema = z.object({
	reason: z.string(),
	ceilingFt: z.number().nullable(),
	visibilitySm: z.number().nullable(),
	stationId: z.string().optional(),
	observedAt: z.string().optional(),
});

export type FoqaApproachTypeBasis = z.infer<typeof FoqaApproachTypeBasisSchema>;

export const FoqaApproachGateSchema = z.object({
	iasKt: z.number().nullable(),
	vspdFpm: z.number().nullable(),
	lateralFt: z.number().nullable(),
	glidepathFt: z.number().nullable(),
	glidepathRef: z.enum(["ils", "rnav", "visual"]).nullable(),
	bankDeg: z.number().nullable(),
	configured: z.boolean().nullable(),
});

/** Evidence-backed airborne time during which the logged autopilot state was disengaged. */
export const FoqaHandFlownTimeSchema = z.object({
	durationSec: z.number().nonnegative(),
	airborneTimeSec: z.number().positive(),
	coverageRatio: z.number().min(0).max(1),
	source: z.literal("afcs_on"),
});

export type FoqaHandFlownTime = z.infer<typeof FoqaHandFlownTimeSchema>;

export const FoqaFlightPhaseSchema = z.enum([
	"departure_taxi",
	"takeoff_roll",
	"rotation",
	"liftoff",
	"crossing_35ft",
	"crossing_1000ft",
	"enroute",
	"descent",
	"approach",
	"touchdown",
	"rollout",
	"arrival_taxi",
]);

export type FoqaFlightPhase = z.infer<typeof FoqaFlightPhaseSchema>;

/**
 * One auditable whole-flight phase boundary. A boundary that cannot be supported by the log
 * carries a machine-readable `refusalReason` and null indices instead of a guessed sample.
 */
export const FoqaPhaseEvidenceSchema = z.object({
	phase: FoqaFlightPhaseSchema.nullable().optional(),
	startIdx: z.number().nullable().optional(),
	endIdx: z.number().nullable().optional(),
	/** ISO strings, not Dates — the jsonb column is never parsed on read (see FoqaTplBasis). */
	startTime: z.string().nullable().optional(),
	endTime: z.string().nullable().optional(),
	/** Which logged channels supported the boundary (e.g. "wow", "ias", "altMsl"). */
	sourceSignals: z.array(z.string()).nullable().optional(),
	confidence: z.number().nullable().optional(),
	refusalReason: z.string().nullable().optional(),
});

export type FoqaPhaseEvidence = z.infer<typeof FoqaPhaseEvidenceSchema>;

/**
 * One AFCS state change between two known (non-null) samples, liftoff through touchdown —
 * retained even when it's too short-lived to be a milestone, so the full on/off history stays
 * auditable (§US-014).
 *
 * Every transition is a BRACKET, never an instant: idx/timestamp/afeFt/iasKt are the first sample
 * known to hold the NEW state and from* is the last sample known to hold the old one. The switch
 * happened between them. When `bracketed` the two are not adjacent — the channel went blank across
 * the switch — so the reported values place the transition no better than that span.
 */
export const FoqaAfcsTransitionSchema = z.object({
	idx: z.number(),
	timestamp: z.string().nullable(),
	toOn: z.boolean(),
	afeFt: z.number().nullable(),
	iasKt: z.number().nullable(),
	/** Last sample known to hold the PRIOR state — the far end of the bracket. Optional only so a
	 *  debrief stored before the bracket existed still parses; every new one carries it. */
	fromIdx: z.number().nullable().optional(),
	fromTimestamp: z.string().nullable().optional(),
	fromAfeFt: z.number().nullable().optional(),
	fromIasKt: z.number().nullable().optional(),
	/** Seconds between the two bracketing known samples: how tightly the switch is placed. */
	bracketSec: z.number().nullable().optional(),
	/** Unknown AFCS samples fall inside the bracket, so no value here is a reading at the switch. */
	bracketed: z.boolean().nullable().optional(),
	/** Seconds the new state held, capped at the milestone hold threshold once met; null when the
	 *  hold was not observed across that window — a gap, or unknown state. A shorter value is the
	 *  whole hold, ended by the next transition. */
	sustainedSec: z.number().nullable(),
	/** sustainedSec at or above the milestone hold threshold — a momentary toggle reads false. */
	sustained: z.boolean(),
});

export type FoqaAfcsTransition = z.infer<typeof FoqaAfcsTransitionSchema>;

/**
 * The far end of a bracketed autopilot milestone. Present only when the AFCS channel went blank
 * across the switch: the milestone's own value is the first known sample of the new state, and the
 * true transition is anywhere back to here — so the height is a range, not a measurement.
 */
export const FoqaAfcsBracketSchema = z.object({
	fromTimestamp: z.string().nullable(),
	fromAfeFt: z.number().nullable(),
	fromIasKt: z.number().nullable(),
	spanSec: z.number().nullable(),
});

export type FoqaAfcsBracket = z.infer<typeof FoqaAfcsBracketSchema>;

/** Shared audit trail for a phase-scoped measurement group; downstream stories populate it. */
export const FoqaMeasurementEvidenceSchema = z.object({
	sourceSignals: z.array(z.string()).nullable().optional(),
	confidence: z.number().nullable().optional(),
	refusalReason: z.string().nullable().optional(),
	/** Per-field refusal reasons, keyed by measurement field name. */
	refusals: z.record(z.string(), z.string()).nullable().optional(),
});

export type FoqaMeasurementEvidence = z.infer<typeof FoqaMeasurementEvidenceSchema>;

/**
 * State at a takeoff instant (rotation onset, liftoff) — informational, never scored.
 * `source` is honest about provenance: "observed_pitch_proxy" for rotation (no parsed
 * format carries a validated nose-wheel discrete), "wow" or "multi_signal" for liftoff.
 */
export const FoqaTakeoffEventSchema = z.object({
	idx: z.number().nullable().optional(),
	/** ISO string, not a Date — the jsonb column is never parsed on read. */
	time: z.string().nullable().optional(),
	iasKt: z.number().nullable().optional(),
	gndSpdKt: z.number().nullable().optional(),
	pitchDeg: z.number().nullable().optional(),
	pitchRateDegPerSec: z.number().nullable().optional(),
	source: z.string().nullable().optional(),
	/** Local sample interval, sec — the true instant lies somewhere within it. */
	samplingUncertaintySec: z.number().nullable().optional(),
	confidence: z.number().nullable().optional(),
	refusalReason: z.string().nullable().optional(),
});

export type FoqaTakeoffEvent = z.infer<typeof FoqaTakeoffEventSchema>;

/**
 * Interpolated crossing of a fixed AFE threshold during climb (35 ft or 1,000 ft) — the
 * same datum and continuity rules produce both (§US-012). `iasKt`/`pitchDeg` are only ever
 * populated at 35 ft; Steve wants the 1,000-ft point for gradient distance alone.
 */
export const FoqaClimbCrossingSchema = z.object({
	idx: z.number().nullable().optional(),
	/** The two contiguous samples the crossing was interpolated between. */
	fromIdx: z.number().nullable().optional(),
	toIdx: z.number().nullable().optional(),
	/** ISO string, not a Date — the jsonb column is never parsed on read. */
	time: z.string().nullable().optional(),
	afeFt: z.number().nullable().optional(),
	lat: z.number().nullable().optional(),
	lon: z.number().nullable().optional(),
	iasKt: z.number().nullable().optional(),
	pitchDeg: z.number().nullable().optional(),
	source: z.string().nullable().optional(),
	/** Width of the sample interval the interpolation drew from, sec. */
	samplingUncertaintySec: z.number().nullable().optional(),
	confidence: z.number().nullable().optional(),
	refusalReason: z.string().nullable().optional(),
});

export type FoqaClimbCrossing = z.infer<typeof FoqaClimbCrossingSchema>;

export const FoqaClimbGradientEndpointsSchema = z.object({
	at35Ft: FoqaClimbCrossingSchema.nullable().optional(),
	at1000Ft: FoqaClimbCrossingSchema.nullable().optional(),
});

export type FoqaClimbGradientEndpoints = z.infer<typeof FoqaClimbGradientEndpointsSchema>;

export const FoqaDepartureMeasurementsSchema = z.object({
	takeoffRollDurationSec: z.number().nullable().optional(),
	takeoffRollDistanceFt: z.number().nullable().optional(),
	takeoffRunAvailableFt: z.number().nullable().optional(),
	takeoffRollPercentOfTora: z.number().nullable().optional(),
	/** Provenance for the runway-relative fields above — which runway/end, and whether
	 * takeoffRunAvailableFt is the declared TORA or the physical-length fallback. */
	takeoffRunwayId: z.string().nullable().optional(),
	takeoffRunwayIdentifier: z.string().nullable().optional(),
	takeoffRunwayEnd: z.enum(["base", "reciprocal"]).nullable().optional(),
	takeoffToraSource: z.enum(["declared", "physical_length_fallback"]).nullable().optional(),
	rotationIasKt: z.number().nullable().optional(),
	rotationRateDegPerSec: z.number().nullable().optional(),
	liftoffIasKt: z.number().nullable().optional(),
	rotation: FoqaTakeoffEventSchema.nullable().optional(),
	liftoff: FoqaTakeoffEventSchema.nullable().optional(),
	distanceTo35Ft: z.number().nullable().optional(),
	iasAt35FtKt: z.number().nullable().optional(),
	pitchAt35FtDeg: z.number().nullable().optional(),
	climbGradient35To1000FtPerNm: z.number().nullable().optional(),
	distance35To1000Nm: z.number().nullable().optional(),
	/** The interpolated 35-ft and 1,000-ft crossings the gradient above was computed from. */
	endpoints: FoqaClimbGradientEndpointsSchema.nullable().optional(),
	/** Fraction of samples between the two crossings with a usable GPS fix. */
	coverage: z.number().nullable().optional(),
	/** Combined interpolation uncertainty of both crossings, sec. */
	uncertainty: z.number().nullable().optional(),
	/** Where the gear and flap SELECTIONS were made — both channels log the lever position, not
	 *  the surface, so these are the crew's calls and precede the surfaces reaching them. */
	gearUpAfeFt: z.number().nullable().optional(),
	gearUpIasKt: z.number().nullable().optional(),
	flapsUpAfeFt: z.number().nullable().optional(),
	flapsUpIasKt: z.number().nullable().optional(),
	/** First sustained AFCS off-to-on transition after liftoff — the departure autopilot milestone. */
	autopilotEngagedAfeFt: z.number().nullable().optional(),
	autopilotEngagedTimestamp: z.string().nullable().optional(),
	autopilotEngagedIasKt: z.number().nullable().optional(),
	autopilotEngagedSource: z.literal("afcs_on").nullable().optional(),
	autopilotEngagedCoverageRatio: z.number().min(0).max(1).nullable().optional(),
	/** Set when the channel went blank across the switch — the values above are then the near end
	 *  of a bracket reaching back to here, not a reading at the transition. */
	autopilotEngagedBracket: FoqaAfcsBracketSchema.nullable().optional(),
	/** Every detected AFCS transition, liftoff through touchdown — see FoqaAfcsTransitionSchema. */
	afcsTransitions: z.array(FoqaAfcsTransitionSchema).nullable().optional(),
	maxIasBelow10000Kt: z.number().nullable().optional(),
	evidence: FoqaMeasurementEvidenceSchema.nullable().optional(),
});

export type FoqaDepartureMeasurements = z.infer<typeof FoqaDepartureMeasurementsSchema>;

/**
 * The band that defined "aligned" for a stored measurement. Persisted per debrief because the
 * numbers are PROVISIONAL — `status` says so — and a later band change must be auditable
 * against what was already measured rather than silently re-interpreting it.
 */
export const FoqaFinalAlignmentToleranceSchema = z.object({
	withinDeg: z.number(),
	sustainedSec: z.number(),
	/** Longest single out-of-tolerance excursion bridged as sensor noise. */
	noiseBridgeSec: z.number(),
	/** Total excursion time bridgeable across one acquisition. */
	noiseBridgeTotalSec: z.number(),
	status: z.literal("provisional_pending_stakeholder_approval"),
});

export type FoqaFinalAlignmentTolerance = z.infer<typeof FoqaFinalAlignmentToleranceSchema>;

/** A point in the arrival where the flight director changed what it was flying. */
export const FoqaProcedureEventSchema = z.object({
	timestamp: z.string().nullable(),
	afeFt: z.number().nullable(),
	iasKt: z.number().nullable(),
	activeWpt: z.string().nullable(),
	rollMode: z.string().nullable(),
	pitchMode: z.string().nullable(),
});

export type FoqaProcedureEvent = z.infer<typeof FoqaProcedureEventSchema>;

/**
 * What the FMS was flying on the arrival, read from the Garmin flight-director modes and active
 * waypoint. Informational — never scored. Absent on Collins logs, which carry no FMS channels.
 */
export const FoqaLoadedProcedureSchema = z.object({
	/** ils: LOC + GS coupled. loc: LOC only. rnav: GPS approach mode through named fixes.
	 *  visual: a Garmin visual approach (FINAL/RWxx fixes only). */
	kind: z.enum(["ils", "loc", "rnav", "visual"]).nullable(),
	navSource: z.string().nullable(),
	/** Distinct active waypoints sequenced from top of descent, in order. */
	fixes: z.array(z.string()),
	/** The RWxx fix whose heading agrees with the runway landed on; null if the FMS never held one. */
	runwayWaypoint: z.string().nullable(),
	/** Selected course on the instrument leg, degrees magnetic. Null for a visual. */
	finalApproachCourseDeg: z.number().nullable(),
	nav1Mhz: z.number().nullable(),
	armedAt: FoqaProcedureEventSchema.nullable(),
	glidepathCapturedAt: FoqaProcedureEventSchema.nullable(),
	/** Lateral guidance left the procedure for a pilot-selected heading before touchdown. */
	abandonedAt: FoqaProcedureEventSchema.nullable(),
	/** An instrument procedure was flown to a runway other than the one landed on. */
	circleToLand: z.boolean().nullable(),
	/** Angle between the procedure's final approach course and the landing runway. */
	circleToLandCourseDiffDeg: z.number().nullable(),
	evidence: FoqaMeasurementEvidenceSchema,
});

export type FoqaLoadedProcedure = z.infer<typeof FoqaLoadedProcedureSchema>;

export const FoqaArrivalMeasurementsSchema = z.object({
	procedure: FoqaLoadedProcedureSchema.nullable().optional(),
	arrivalMaxIasBelow10000Kt: z.number().nullable().optional(),
	finalAlignmentDistanceFromThresholdNm: z.number().nullable().optional(),
	finalAlignmentAfeFt: z.number().nullable().optional(),
	/** ISO string, not a Date — the jsonb column is never parsed on read (see FoqaTplBasis). */
	finalAlignmentTime: z.string().nullable().optional(),
	finalAlignmentTolerance: FoqaFinalAlignmentToleranceSchema.nullable().optional(),
	/** Set when the aligned run reached the oldest sample the search could see: the distance
	 *  above is then a FLOOR, bounded by this radius in nm, not the acquisition point itself. */
	finalAlignmentBoundedByWindowNm: z.number().nullable().optional(),
	finalAlignmentEvidence: FoqaMeasurementEvidenceSchema.nullable().optional(),
	/** Last sustained AFCS on-to-off transition before touchdown — the approach autopilot milestone. */
	autopilotDisengagedAfeFt: z.number().nullable().optional(),
	autopilotDisengagedTimestamp: z.string().nullable().optional(),
	autopilotDisengagedIasKt: z.number().nullable().optional(),
	autopilotDisengagedSource: z.literal("afcs_on").nullable().optional(),
	autopilotDisengagedCoverageRatio: z.number().min(0).max(1).nullable().optional(),
	/** Set when the channel went blank across the switch — see autopilotEngagedBracket. */
	autopilotDisengagedBracket: FoqaAfcsBracketSchema.nullable().optional(),
	/** Same full transition list as departure.afcsTransitions — self-contained for this milestone. */
	afcsTransitions: z.array(FoqaAfcsTransitionSchema).nullable().optional(),
	evidence: FoqaMeasurementEvidenceSchema.nullable().optional(),
});

export type FoqaArrivalMeasurements = z.infer<typeof FoqaArrivalMeasurementsSchema>;

export const FoqaTaxiMeasurementsSchema = z.object({
	departureMaxTaxiSpeedKt: z.number().nullable().optional(),
	arrivalMaxTaxiSpeedKt: z.number().nullable().optional(),
	departureEvidence: FoqaMeasurementEvidenceSchema.nullable().optional(),
	arrivalEvidence: FoqaMeasurementEvidenceSchema.nullable().optional(),
});

export type FoqaTaxiMeasurements = z.infer<typeof FoqaTaxiMeasurementsSchema>;

/** Rollout-specific evidence: the deceleration channel and its coverage, the speed
 *  thresholds applied, the sample indices behind each figure, and per-field refusals. */
export const FoqaRolloutEvidenceSchema = z.object({
	decelerationSourceSignals: z.array(z.string()).nullable().optional(),
	/** Fraction (0-1) of the deceleration window carrying the source channel. */
	decelerationCoverage: z.number().nullable().optional(),
	/** Which declared distance runway remaining was measured against, and its value. */
	runwayRemainingBasis: z.object({
		availableFt: z.number(),
		basis: z.enum(["lda", "physical_length"]),
	}).nullable().optional(),
	/** Average g the unvalidated longAc channel would have given over the same window — kept
	 *  for a future comparison against groundspeed, never rendered or scored. */
	unvalidatedLongAcAverageG: z.number().nullable().optional(),
	thresholds: z.object({
		crossingSpeedKt: z.number(),
		taxiSpeedKt: z.number(),
		sustainedWindowSec: z.number(),
	}).nullable().optional(),
	phaseIndices: z.object({
		touchdownIdx: z.number().nullable(),
		crossing70KtIdx: z.number().nullable(),
		taxiSpeedIdx: z.number().nullable(),
		/** First sample, if any, whose position falls outside the runway edges. */
		offRunwayIdx: z.number().nullable(),
	}).nullable().optional(),
	/** Per-field refusal reasons, keyed by measurement field name. */
	refusals: z.record(z.string(), z.string()).nullable().optional(),
});

export type FoqaRolloutEvidence = z.infer<typeof FoqaRolloutEvidenceSchema>;

export const FoqaRolloutMeasurementsSchema = z.object({
	touchdownTo70KtsFt: z.number().nullable().optional(),
	touchdownToTaxiSpeedFt: z.number().nullable().optional(),
	rolloutDurationSec: z.number().nullable().optional(),
	runwayUsedFt: z.number().nullable().optional(),
	runwayRemainingAtTaxiSpeedFt: z.number().nullable().optional(),
	peakDecelerationG: z.number().nullable().optional(),
	sustainedPeakDecelerationG: z.number().nullable().optional(),
	averageDecelerationG: z.number().nullable().optional(),
	decelerationSource: z.string().nullable().optional(),
	evidence: FoqaRolloutEvidenceSchema.nullable().optional(),
});

export type FoqaRolloutMeasurements = z.infer<typeof FoqaRolloutMeasurementsSchema>;

export const FoqaMeasurementsSchema = z.object({
	approachType: FoqaApproachTypeSchema,
	thresholdIdx: z.number(),
	touchdownIdx: z.number(),
	aglOverThresholdFt: z.number().nullable(),
	iasOverThresholdKt: z.number().nullable(),
	touchdownDistanceFromThresholdFt: z.number(),
	touchdownDistanceFromCenterlineFt: z.number(),
	iasAtTouchdownKt: z.number().nullable(),
	finalVspdMinFpm: z.number().nullable(),
	finalVspdMaxFpm: z.number().nullable(),
	ias500to50MinKt: z.number().nullable(),
	ias500to50MaxKt: z.number().nullable(),
	maxAbsCenterlineDevDeg: z.number().nullable(),
	maxBankBelow1000Deg: z.number().nullable(),
	maxBankBelow200Deg: z.number().nullable(),
	maxPitchBelow1000Deg: z.number().nullable(),
	minPitchBelow1000Deg: z.number().nullable(),
	maxPitchBelow200Deg: z.number().nullable(),
	minPitchBelow200Deg: z.number().nullable(),
	minPitchBelow50Deg: z.number().nullable(),
	minPitchBelow20Deg: z.number().nullable(),
	maxIasBelow1000Kt: z.number().nullable(),
	maxIasBelow500Kt: z.number().nullable(),
	maxIasBelow200Kt: z.number().nullable(),
	minIasBelow200Kt: z.number().nullable(),
	vertAccelAtTouchdownG: z.number().nullable(),
	sideLoadAtTouchdownG: z.number().nullable(),
	fullyConfiguredAfeFt: z.number().nullable(),
	maxOutsideExtendedRunwayBelow200Ft: z.number().nullable(),
	maxBankInFlightDeg: z.number().nullable(),
	maxIasInFlightKt: z.number().nullable().optional(),
	maxMachInFlight: z.number().nullable().optional(),
	/** AFM operating limits from the model profile at scoring time — pinned so a profile change
	 *  cannot re-flag history. Absent on debriefs scored before the limits shipped. */
	limits: FoqaOperatingLimitsSchema.nullable().optional(),
	maxIasBelow10000Kt: z.number().nullable(),
	maxTaxiSpeedKt: z.number().nullable(),
	/** Omitted for legacy, truncated, or non-Garmin logs; null when current analysis cannot support it. */
	handFlownTime: FoqaHandFlownTimeSchema.nullable().optional(),
	runwayRemainingAtTouchdownFt: z.number().nullable(),
	/** Touchdown margin against the Touchdown Point Limit; negative means past the limit. */
	tplMarginFt: z.number().nullable().optional(),
	tplFt: z.number().nullable().optional(),
	/** Why TPL could not be computed, when it could not — drives the runway-remaining fallback. */
	tplRefusal: z.string().nullable().optional(),
	tplBasis: FoqaTplBasisSchema.nullable().optional(),
	/** Flight facts retained so a Vref correction can recompute, not merely re-score, TPL. */
	tplInput: FoqaTplInputSchema.nullable().optional(),
	/** Why this approach was called visual or instrument — see visualWeatherFrom. */
	approachTypeBasis: FoqaApproachTypeBasisSchema.nullable().optional(),
	runwayRemainingAt70KtsFt: z.number().nullable(),
	iasAt50AglKt: z.number().nullable(),
	verticalSpeedConsistentFpm: z.number().nullable(),
	ilsGlideslopeDeviationDots: z.number().nullable(),
	ilsLocalizerDeviationDots: z.number().nullable(),
	gpsCourseDeviationDots: z.number().nullable(),
	vorCourseDeviationDeg: z.number().nullable(),
	vgsiState: z.enum(["above", "below", "on"]).nullable(),
	nonCoincidentGlidepath: z.boolean(),
	/** Historical only. Circling detection is retired -- a circle-to-land is a visual approach --
	 *  but measurements stored before that still carry these, so the shape has to accept them. */
	circling: z.boolean().optional(),
	circlingReason: z.string().nullable().optional(),
	circlingConfidence: z.number().nullable().optional(),
	maxCirclingRadiusNm: z.number().nullable().optional(),
	maxCirclingIasKt: z.number().nullable().optional(),
	circlingIas1000to500MaxKt: z.number().nullable().optional(),
	circlingIas1000to500MinKt: z.number().nullable().optional(),
	ias1000to500MaxKt: z.number().nullable().optional(),
	ias1000to500MinKt: z.number().nullable().optional(),
	worstSinkBelow1000Ft: z.number().nullable().optional(),
	worstSinkBelow200Ft: z.number().nullable().optional(),
	crossingAltitudeBaroFt: z.number().nullable().optional(),
	trackOverThresholdDeg: z.number().nullable().optional(),
	gates: z.object({
		at1000: FoqaApproachGateSchema.nullable(),
		at500: FoqaApproachGateSchema.nullable(),
		at200: FoqaApproachGateSchema.nullable(),
	}).nullable().optional(),
	/** Whole-flight phase boundaries. Informational only — never an input to scoring or flags. */
	phases: z.array(FoqaPhaseEvidenceSchema).nullable().optional(),
	departure: FoqaDepartureMeasurementsSchema.nullable().optional(),
	arrival: FoqaArrivalMeasurementsSchema.nullable().optional(),
	taxi: FoqaTaxiMeasurementsSchema.nullable().optional(),
	rollout: FoqaRolloutMeasurementsSchema.nullable().optional(),
	exceedanceSeries: z.object({
		approach: z.array(FoqaExceedanceSeriesPointSchema),
		entireFlight: z.array(FoqaExceedanceSeriesPointSchema),
	}),
});

export type FoqaMeasurements = z.infer<typeof FoqaMeasurementsSchema>;

export const FoqaDebriefOverridesSchema = z.object({
	vref: z.number().nullable().optional(),
	/** Pilot-entered rotation speed. Displayed against the observed rotation; never scored. */
	vr: z.number().nullable().optional(),
	approachType: FoqaApproachTypeSchema.nullable().optional(),
	circling: z.boolean().nullable().optional(),
});

export type FoqaDebriefOverrides = z.infer<typeof FoqaDebriefOverridesSchema>;

/** How the current debrief was produced: machine analysis vs a human correction. */
export const FoqaDebriefSourceSchema = z.enum(["computed", "corrected"]);
export type FoqaDebriefSource = z.infer<typeof FoqaDebriefSourceSchema>;

export const CJP_EXCEEDANCE_EVENTS: FoqaThresholdParam[] = [
	{
		key: "pitch_high_below_1000",
		label: "Pitch: high below 1000'",
		phase: "approach_landing",
		unit: "deg",
		weight: 0,
		entity: "pitch",
		segment: "approach",
		belowAfe: 1000,
		durationSec: 3,
		safetyEvent: {
			max: 30, segment: "approach", entity: "pitch", belowAfe: 1000, durationSec: 3, caution: 20, warning: 30, dir: "above",
		},
	},
	{
		key: "pitch_high_below_200",
		label: "Pitch: high below 200'",
		phase: "approach_landing",
		unit: "deg",
		weight: 0,
		entity: "pitch",
		segment: "approach",
		belowAfe: 200,
		durationSec: 3,
		safetyEvent: {
			max: 20, segment: "approach", entity: "pitch", belowAfe: 200, durationSec: 3, caution: 17, warning: 20, dir: "above",
		},
	},
	{
		key: "pitch_low_below_1000",
		label: "Pitch: low below 1000'",
		phase: "approach_landing",
		unit: "deg",
		weight: 0,
		entity: "pitch",
		segment: "approach",
		belowAfe: 1000,
		durationSec: 3,
		safetyEvent: {
			min: -15, segment: "approach", entity: "pitch", belowAfe: 1000, durationSec: 3, caution: -10, warning: -15, dir: "below",
		},
	},
	{
		key: "pitch_low_below_200",
		label: "Pitch: low below 200'",
		phase: "approach_landing",
		unit: "deg",
		weight: 0,
		entity: "pitch",
		segment: "approach",
		belowAfe: 200,
		durationSec: 2,
		safetyEvent: {
			min: -13, segment: "approach", entity: "pitch", belowAfe: 200, durationSec: 2, caution: -7, warning: -13, dir: "below",
		},
	},
	{
		key: "pitch_low_below_50",
		label: "Pitch: low below 50'",
		phase: "approach_landing",
		unit: "deg",
		weight: 0,
		entity: "pitch",
		segment: "approach",
		belowAfe: 50,
		durationSec: 1,
		safetyEvent: {
			min: -10, segment: "approach", entity: "pitch", belowAfe: 50, durationSec: 1, caution: -6, warning: -10, dir: "below",
		},
	},
	{
		key: "pitch_low_below_20",
		label: "Pitch: low below 20'",
		phase: "approach_landing",
		unit: "deg",
		weight: 0,
		entity: "pitch",
		segment: "approach",
		belowAfe: 20,
		durationSec: 1,
		safetyEvent: {
			min: -6, segment: "approach", entity: "pitch", belowAfe: 20, durationSec: 1, caution: -3, warning: -6, dir: "below",
		},
	},
	{
		key: "bank_max_below_1000",
		label: "Bank: max below 1000'",
		phase: "approach_landing",
		unit: "deg",
		weight: 0,
		entity: "bank",
		segment: "approach",
		belowAfe: 1000,
		durationSec: 3,
		safetyEvent: {
			max: 50, segment: "approach", entity: "bank", belowAfe: 1000, durationSec: 3, caution: 45, warning: 50, dir: "above",
		},
	},
	{
		key: "bank_max_below_200",
		label: "Bank: max below 200'",
		phase: "approach_landing",
		unit: "deg",
		weight: 0,
		entity: "bank",
		segment: "approach",
		belowAfe: 200,
		durationSec: 3,
		safetyEvent: {
			max: 40, segment: "approach", entity: "bank", belowAfe: 200, durationSec: 3, caution: 30, warning: 40, dir: "above",
		},
	},
	{
		key: "fully_configured_afe",
		label: "Fully configured: AFE",
		phase: "approach_landing",
		unit: "ft",
		weight: 0,
		entity: "afe",
		segment: "approach",
		safetyEvent: {
			min: 500, segment: "approach", entity: "afe", caution: 700, warning: 500, dir: "below",
		},
	},
	{
		key: "ias_vref_high_below_200",
		label: "IAS-Vref: high below 200'",
		phase: "approach_landing",
		unit: "kt",
		weight: 0,
		entity: "iasMinusVref",
		segment: "approach",
		belowAfe: 200,
		durationSec: 2,
		safetyEvent: {
			max: 25, relativeTo: "vref", segment: "approach", entity: "iasMinusVref", belowAfe: 200, durationSec: 2, caution: 20, warning: 25, dir: "above",
		},
	},
	{
		key: "ias_vref_low_below_200",
		label: "IAS-Vref: low below 200'",
		phase: "approach_landing",
		unit: "kt",
		weight: 0,
		entity: "iasMinusVref",
		segment: "approach",
		belowAfe: 200,
		durationSec: 2,
		safetyEvent: {
			min: -12, relativeTo: "vref", segment: "approach", entity: "iasMinusVref", belowAfe: 200, durationSec: 2, caution: -8, warning: -12, dir: "below",
		},
	},
	{
		key: "outside_extended_runway_below_200",
		label: "Outside extended RW below 200'",
		phase: "approach_landing",
		unit: "ft",
		weight: 0,
		entity: "rwy_lateral",
		segment: "approach",
		belowAfe: 200,
		durationSec: 1,
		safetyEvent: {
			max: 100, segment: "approach", entity: "rwy_lateral", belowAfe: 200, durationSec: 1, caution: 50, warning: 100, dir: "above",
		},
	},
	{
		key: "runway_remaining_at_70kts",
		label: "RWY remaining @70 kts",
		phase: "approach_landing",
		unit: "ft",
		weight: 0,
		entity: "rwy",
		segment: "approach",
		safetyEvent: {
			min: 1000, segment: "approach", entity: "rwy", caution: 1300, warning: 1000, dir: "below",
		},
	},
	{
		// CJP workbook: caution inside the AFM limit +10 kt (Mach: the 10-kt equivalent, ~+0.03) for
		// more than 10 s; warning beyond that. Relative to the model limit, so one row serves every model.
		key: "max_flight_speed",
		label: "Max flight speed (Vmo)",
		phase: "general",
		unit: "kt",
		weight: 0,
		entity: "ias",
		segment: "entire_flight",
		durationSec: 10,
		safetyEvent: {
			relativeTo: "vmo", segment: "entire_flight", entity: "ias", durationSec: 10, caution: 0, warning: 10, dir: "above",
		},
	},
	{
		key: "max_flight_mach",
		label: "Max flight Mach (Mmo)",
		phase: "general",
		unit: "mach",
		weight: 0,
		entity: "mach",
		segment: "entire_flight",
		durationSec: 10,
		safetyEvent: {
			relativeTo: "mmo", segment: "entire_flight", entity: "mach", durationSec: 10, caution: 0, warning: 0.03, dir: "above",
		},
	},
	{
		key: "max_bank_in_flight",
		label: "Max bank in flight",
		phase: "general",
		unit: "deg",
		weight: 0,
		entity: "bank",
		segment: "entire_flight",
		durationSec: 3,
		safetyEvent: {
			max: 70, segment: "entire_flight", entity: "bank", durationSec: 3, caution: 55, warning: 70, dir: "above",
		},
	},
	{
		key: "max_speed_below_10000",
		label: "Max speed below 10000'",
		phase: "general",
		unit: "kt",
		weight: 0,
		entity: "ias",
		segment: "entire_flight",
		durationSec: 10,
		safetyEvent: {
			max: 280, segment: "entire_flight", entity: "ias", durationSec: 10, caution: 265, warning: 280, dir: "above",
		},
	},
	{
		key: "max_taxi_speed",
		label: "Max taxi speed",
		phase: "approach_landing",
		unit: "kt",
		weight: 0,
		entity: "gs",
		segment: "entire_flight",
		durationSec: 5,
		safetyEvent: {
			max: 35, segment: "entire_flight", entity: "gs", durationSec: 5, caution: 28, warning: 35, dir: "above",
		},
	},
	{
		key: "sink_rate_max_below_1000",
		label: "Sink rate: max below 1000'",
		phase: "approach_landing",
		unit: "fpm",
		weight: 0,
		entity: "vs",
		segment: "approach",
		belowAfe: 1000,
		durationSec: 3,
		safetyEvent: {
			min: -2500, segment: "approach", entity: "vs", belowAfe: 1000, durationSec: 3, caution: -2000, warning: -2500, dir: "below",
		},
	},
	{
		key: "sink_rate_max_below_1000_6s",
		label: "Sink rate: max below 1000' (6s)",
		phase: "approach_landing",
		unit: "fpm",
		weight: 0,
		entity: "vs",
		segment: "approach",
		belowAfe: 1000,
		durationSec: 6,
		safetyEvent: {
			min: -2000, segment: "approach", entity: "vs", belowAfe: 1000, durationSec: 6, caution: -1500, warning: -2000, dir: "below",
		},
	},
	{
		key: "sink_rate_max_below_200",
		label: "Sink rate: max below 200'",
		phase: "approach_landing",
		unit: "fpm",
		weight: 0,
		entity: "vs",
		segment: "approach",
		belowAfe: 200,
		durationSec: 3,
		safetyEvent: {
			min: -1500, segment: "approach", entity: "vs", belowAfe: 200, durationSec: 3, caution: -1000, warning: -1500, dir: "below",
		},
	},
	{
		key: "touchdown_distance_short",
		label: "Touchdown distance: short",
		phase: "approach_landing",
		unit: "ft",
		weight: 0,
		entity: "",
		segment: "approach",
		safetyEvent: {
			min: 100, segment: "approach", caution: 300, warning: 100, dir: "below",
		},
	},
	{
		key: "ias_vref_low_at_threshold",
		label: "THLD: low IAS-Vref",
		phase: "approach_landing",
		unit: "kt",
		weight: 0,
		entity: "iasMinusVref",
		segment: "approach",
		safetyEvent: {
			min: -15, relativeTo: "vref", segment: "approach", entity: "iasMinusVref", caution: -10, warning: -15, dir: "below",
		},
	},
];

const SCORED_PARAMS: FoqaThresholdParam[] = [
	{
		key: "airspeed_over_threshold",
		label: "Airspeed over threshold",
		phase: "approach_landing",
		unit: "kt",
		weight: 7,
		entity: "ias",
		bands: {
			green: [-5, 10], yellow: [[-15, -5], [10, 25]], relativeTo: "vref", penalty: { kind: "linearRel", k: 7.5 },
		},
		safetyEvent: {
			max: 25,
			relativeTo: "vref",
			segment: "approach",
			entity: "iasMinusVref",
			caution: 20,
			warning: 25,
			dir: "above",
		},
	},
	{
		key: "touchdown_distance_from_threshold",
		label: "Touchdown distance from threshold",
		phase: "approach_landing",
		unit: "ft",
		weight: 4,
		bands: {
			// Distance from the 1000 ft AIMING POINT, which is the reference pilots are trained to
			// and the only one a ±700 ft tolerance makes sense against. Measuring it from the
			// threshold was our error: real touchdowns median ~1011 ft, so normal technique scored
			// yellow while the green range covered touching down SHORT of the runway. Same width,
			// shifted +1000; `target` renders the 1000 ft mark as the goal on the debrief.
			green: [400, 1800], yellow: [[-1000, 400], [1800, 6000]], target: 1000, penalty: { kind: "linearRel", k: 31.1 },
		},
		safetyEvent: {
			max: 4000,
			segment: "approach",
			caution: 3000,
			warning: 4000,
			dir: "above",
		},
	},
	{
		key: "sink_rate_on_final",
		label: "Sink rate within range",
		phase: "approach_landing",
		unit: "fpm",
		weight: 3,
		entity: "vs",
		appliesTo: "visual",
		bands: {
			green: [-1200, -200], yellow: [[-1500, -1200], [-200, 0]], penalty: { kind: "quadraticRel", k: 100 },
		},
		safetyEvent: {
			min: -1500,
			segment: "approach",
			entity: "vs",
			caution: -1300,
			warning: -1500,
			dir: "below",
			belowAfe: 500,
			durationSec: 3,
		},
	},
	{
		key: "airspeed_at_touchdown",
		label: "Airspeed at touchdown",
		phase: "approach_landing",
		unit: "kt",
		// One row for both approach types — the variants read the same `iasAtTouchdownKt` against
		// byte-identical bands. Takes the IAP weight (4, not the visual 2): 12 of the 13 real
		// approaches we can score are instrument, so this preserves the status quo where it
		// actually applies rather than halving it there.
		weight: 4,
		entity: "ias",
		bands: {
			green: [-15, 10], yellow: [[-25, -15], [10, 25]], relativeTo: "vref", penalty: { kind: "linearRel", k: 16.67 },
		},
	},
	{
		key: "agl_over_threshold",
		label: "Threshold crossing height",
		phase: "approach_landing",
		unit: "ft",
		weight: 7,
		entity: "afe",
		bands: {
			// CJP workbook "Threshold Height": target 50, green 35–60, yellow 20–35 / 61–75,
			// red < 20 or > 75. The prior [0, 30] band was CloudAhoy's TCH-RELATIVE grader
			// ("THLD crossing AGL (TCH+)") applied to our ABSOLUTE measurement — which scored
			// low crossings green and dinged ideal ~50 ft ones. k puts the score at 50 by the
			// yellow/red boundary (15 ft past green on either side).
			green: [35, 60], yellow: [[20, 35], [60, 75]], target: 50, penalty: { kind: "linearRel", k: 83.33 },
		},
	},
	{
		key: "touchdown_distance_from_centerline",
		label: "Touchdown distance from centerline",
		phase: "approach_landing",
		unit: "ft",
		// One row for both approach types: the visual and IAP variants read the same expression
		// (`|touchdownDistanceFromCenterlineFt|`) off the same sample, so there was never a
		// measurement difference for the split to express — only two bands and two weights.
		weight: 2,
		bands: {
			// k rescaled to the green width so the score still reaches 0 at the yellow outer edge,
			// as it did at 300 ft under the old ±30 band.
			green: [0, 5], yellow: [[5, 10]], target: 0, gradedInGreen: true, penalty: { kind: "linearRel", k: 95 },
		},
	},
	{
		key: "airspeed_at_50_agl",
		label: "Airspeed at 50' AGL",
		phase: "approach_landing",
		unit: "kt",
		weight: 0,
		entity: "ias",
		appliesTo: "visual",
		bands: {
			green: [-5, 10], yellow: [[-25, -5], [10, 100]], relativeTo: "vref", penalty: { kind: "linearRel", k: 7.5 },
		},
	},
	{
		key: "vertical_accel_at_touchdown",
		label: "Vertical acceleration at touchdown",
		phase: "approach_landing",
		unit: "g",
		// One row for both approach types. Advisory either way, so the collapse cannot move a score
		// — it only stops the calibration corpus being split across two identical definitions.
		weight: 0,
		entity: "vacc",
		bands: {
			green: [1, 1.3], yellow: [[1.3, 10]], penalty: { kind: "linearRel", k: 3.45 },
		},
	},
	{
		key: "side_loading_at_touchdown",
		label: "Side loading at touchdown",
		phase: "approach_landing",
		unit: "g",
		weight: 0,
		entity: "lacc",
		bands: {
			green: [0, 0.3], yellow: [[-10, 0]], target: 0, gradedInGreen: true, penalty: { kind: "linearRel", k: 0.92 }, 
		},
	},
	{
		key: "tpl_margin",
		label: "Touchdown margin to the Touchdown Point Limit",
		phase: "approach_landing",
		unit: "ft",
		// Advisory: TPL is a LIMIT, so its teeth are the safety event below. Keeping the band at
		// weight 0 also keeps scores comparable with flights that fall back to
		// runway_remaining_at_touchdown when TPL cannot be computed.
		weight: 0,
		bands: {
			green: [0, 20000], yellow: [[-1000, 0]],
		},
		safetyEvent: {
			segment: "approach",
			dir: "below",
			caution: 200,
			warning: 0,
		},
	},
	{
		key: "runway_remaining_at_touchdown",
		label: "Runway remaining at touchdown",
		phase: "approach_landing",
		unit: "ft",
		weight: 0,
		entity: "rwy",
		bands: {
			// A positive FLOOR, calibrated off the runway-remaining-@70kt flag (caution 1300 /
			// warning 1000) and CJP's per-model SOP_MRR_TD of 1400 ft. The old band graded degrees
			// of OVERRUN — every point of resolution was spent on touchdowns beyond the departure
			// end, while 500 ft remaining and 9,000 ft remaining both scored 100.
			// Upper bound stays above any real runway so long ones cannot flag; k puts the score at
			// 0 with no runway left at all.
			green: [1400, 20000], yellow: [[1000, 1400]], penalty: { kind: "linearRel", k: 1329 },
		},
	},
	{
		key: "ils_glideslope_deviation",
		label: "ILS glideslope deviation",
		phase: "approach_landing",
		unit: "dots",
		weight: 3,
		entity: "gs_dots",
		appliesTo: "instrument",
		bands: {
			green: [-1, 1], yellow: [[-2, -1], [1, 2]], target: 0, gradedInGreen: true, penalty: { kind: "linearRel", k: 100 }, 
		},
	},
	{
		key: "ils_localizer_deviation",
		label: "ILS localizer deviation",
		phase: "approach_landing",
		unit: "dots",
		weight: 3,
		entity: "loc_dots",
		appliesTo: "instrument",
		bands: {
			green: [-1, 1], yellow: [[-2, -1], [1, 2]], target: 0, gradedInGreen: true, penalty: { kind: "linearRel", k: 100 }, 
		},
	},
	{
		key: "gps_course_deviation",
		enabled: false,
		label: "Deviation from GPS course after FAF",
		phase: "approach_landing",
		unit: "dots",
		weight: 6,
		entity: "gps_dots",
		// NOT moved to "both" with the ILS pair: this falls back to a cross-track geometric
		// proxy on any non-ILS approach (approach.ts:692), so it is computed even where no
		// lateral guidance was being flown. Scoring it on visual approaches would invent the
		// deviations this classification change exists to stop.
		appliesTo: "instrument",
		bands: {
			green: [-1, 1], yellow: [[-2, -1], [1, 2]], target: 0, gradedInGreen: true, penalty: { kind: "linearRel", k: 100 }, 
		},
	},
	{
		key: "vor_course_deviation",
		// Disabled with gps_course_deviation: this one is a PROXY that reuses the centerline
		// measurement rather than a real VOR source.
		enabled: false,
		label: "Deviation from VOR course after FAF",
		phase: "approach_landing",
		unit: "deg",
		weight: 6,
		entity: "vor_deg",
		appliesTo: "instrument",
		bands: {
			green: [-5, 5], yellow: [[-20, -5], [5, 20]], target: 0, gradedInGreen: true, penalty: { kind: "linearRel", k: 66.7 }, 
		},
	},
	// Read at the instant of crossing, not the worst value below it — the extrema stay in flags.
	// 1,000 ft carries no weight: a pattern is often still on base there, where course and path
	// mean nothing, so configured is the only question asked.
	{
		key: "gate_1000_configured",
		label: "Configured by 1,000 ft",
		phase: "approach_landing",
		gate: "gate_1000",
		unit: "",
		weight: 0,
		entity: "configured",
		bands: {
			green: [1, 1], yellow: [[0, 0]],
		},
	},
	{
		key: "gate_1000_airspeed",
		label: "Airspeed at 1,000 ft",
		phase: "approach_landing",
		gate: "gate_1000",
		unit: "kt",
		weight: 0,
		entity: "ias",
		bands: {
			green: [0, 25], yellow: [[-10, 0], [25, 40]], relativeTo: "vref", penalty: { kind: "linearRel", k: 12.5 },
		},
	},
	{
		key: "gate_1000_vertical_speed",
		label: "Vertical speed at 1,000 ft",
		phase: "approach_landing",
		gate: "gate_1000",
		unit: "fpm",
		weight: 0,
		entity: "vs",
		bands: {
			green: [-1200, 200], yellow: [[-1500, -1200], [200, 500]], penalty: { kind: "quadraticRel", k: 100 },
		},
	},
	{
		key: "gate_1000_lateral",
		label: "Lateral position at 1,000 ft",
		phase: "approach_landing",
		gate: "gate_1000",
		unit: "ft",
		weight: 0,
		entity: "lateral_ft",
		bands: {
			green: [-500, 500], yellow: [[-1000, -500], [500, 1000]], target: 0, gradedInGreen: true, penalty: { kind: "linearRel", k: 100 },
		},
	},
	{
		key: "gate_1000_glidepath",
		label: "Glidepath at 1,000 ft",
		phase: "approach_landing",
		gate: "gate_1000",
		unit: "ft",
		weight: 0,
		entity: "glidepath_ft",
		bands: {
			green: [-200, 200], yellow: [[-400, -200], [200, 400]], target: 0, gradedInGreen: true, penalty: { kind: "linearRel", k: 100 },
		},
	},
	{
		key: "gate_1000_bank",
		label: "Bank angle at 1,000 ft",
		phase: "approach_landing",
		gate: "gate_1000",
		unit: "deg",
		weight: 0,
		entity: "roll",
		bands: {
			green: [0, 25], yellow: [[25, 35]], penalty: { kind: "linearRel", k: 10 },
		},
	},
	{
		key: "gate_500_airspeed",
		label: "Airspeed at 500 ft",
		phase: "approach_landing",
		gate: "gate_500",
		unit: "kt",
		weight: 3,
		entity: "ias",
		bands: {
			green: [-5, 10], yellow: [[-15, -5], [10, 25]], relativeTo: "vref", penalty: { kind: "linearRel", k: 12.5 },
		},
	},
	{
		key: "gate_500_vertical_speed",
		label: "Vertical speed at 500 ft",
		phase: "approach_landing",
		gate: "gate_500",
		unit: "fpm",
		weight: 2,
		entity: "vs",
		bands: {
			green: [-1000, 100], yellow: [[-1500, -1000], [100, 300]], penalty: { kind: "quadraticRel", k: 100 },
		},
	},
	{
		key: "gate_500_lateral",
		label: "Lateral position at 500 ft",
		phase: "approach_landing",
		gate: "gate_500",
		unit: "ft",
		weight: 1,
		entity: "lateral_ft",
		bands: {
			green: [-75, 75], yellow: [[-150, -75], [75, 150]], target: 0, gradedInGreen: true, penalty: { kind: "linearRel", k: 33.3 },
		},
	},
	{
		key: "gate_500_glidepath",
		label: "Glidepath at 500 ft",
		phase: "approach_landing",
		gate: "gate_500",
		unit: "ft",
		weight: 3,
		entity: "glidepath_ft",
		bands: {
			green: [-50, 50], yellow: [[-100, -50], [50, 100]], target: 0, gradedInGreen: true, penalty: { kind: "linearRel", k: 33.3 },
		},
	},
	{
		key: "gate_500_bank",
		label: "Bank angle at 500 ft",
		phase: "approach_landing",
		gate: "gate_500",
		unit: "deg",
		weight: 1,
		entity: "roll",
		bands: {
			green: [0, 10], yellow: [[10, 20]], penalty: { kind: "linearRel", k: 10 },
		},
	},
	{
		key: "gate_200_airspeed",
		label: "Airspeed at 200 ft",
		phase: "approach_landing",
		gate: "gate_200",
		unit: "kt",
		weight: 5,
		entity: "ias",
		bands: {
			green: [-5, 10], yellow: [[-15, -5], [10, 25]], relativeTo: "vref", penalty: { kind: "linearRel", k: 12.5 },
		},
	},
	{
		key: "gate_200_vertical_speed",
		label: "Vertical speed at 200 ft",
		phase: "approach_landing",
		gate: "gate_200",
		unit: "fpm",
		weight: 3,
		entity: "vs",
		bands: {
			green: [-1000, 100], yellow: [[-1500, -1000], [100, 300]], penalty: { kind: "quadraticRel", k: 100 },
		},
	},
	{
		key: "gate_200_lateral",
		label: "Lateral position at 200 ft",
		phase: "approach_landing",
		gate: "gate_200",
		unit: "ft",
		weight: 2,
		entity: "lateral_ft",
		bands: {
			green: [-50, 50], yellow: [[-100, -50], [50, 100]], target: 0, gradedInGreen: true, penalty: { kind: "linearRel", k: 33.3 },
		},
	},
	{
		key: "gate_200_glidepath",
		label: "Glidepath at 200 ft",
		phase: "approach_landing",
		gate: "gate_200",
		unit: "ft",
		weight: 5,
		entity: "glidepath_ft",
		bands: {
			green: [-50, 50], yellow: [[-100, -50], [50, 100]], target: 0, gradedInGreen: true, penalty: { kind: "linearRel", k: 33.3 },
		},
	},
	{
		key: "gate_200_bank",
		label: "Bank angle at 200 ft",
		phase: "approach_landing",
		gate: "gate_200",
		unit: "deg",
		weight: 1,
		entity: "roll",
		bands: {
			green: [0, 10], yellow: [[10, 20]], penalty: { kind: "linearRel", k: 10 },
		},
	},
	{
		key: "crossing_altitudes_baro",
		label: "Crossing altitudes maintained (Baro)",
		phase: "approach_landing",
		unit: "ft",
		weight: 5,
		entity: "afe",
		appliesTo: "instrument",
		bands: {
			green: [-100, 9999], yellow: [[-300, -100]],
		},
	},
	{
		key: "track_over_threshold",
		// Key unchanged so existing config rows keep resolving; the measurement moved to touchdown.
		// Applies to every landing: unlike the beam parameters this asks whether the aircraft was
		// straight when the wheels touched, which is a question about the landing, not a procedure.
		label: "Runway alignment at touchdown",
		phase: "approach_landing",
		unit: "deg",
		weight: 3,
		bands: {
			green: [-1.5, 1.5], yellow: [[-20, -1.5], [1.5, 20]],
		},
	},
	{
		key: "sink_rate_on_final_ifr",
		label: "Sink rate range in IAP",
		phase: "approach_landing",
		unit: "fpm",
		weight: 4,
		entity: "vs",
		appliesTo: "instrument",
		bands: {
			green: [-1000, -300], yellow: [[-4000, -1000], [-300, 1000]],
		},
	},
];

/**
 * The whole-flight metrics, as UNWEIGHTED informational params: measured and shown, never
 * graded (weight 0, no bands, no safetyEvent), so none can move a score or a flag — they may
 * earn weights later. Keys match the persisted measurement fields (camelCase past the US-002
 * splits) so the scorecard, the phase rows and the migration all resolve the same name.
 * Appended AFTER the scored params — the config migration relies on this order.
 */
export const FOQA_WHOLE_FLIGHT_INFORMATIONAL_PARAMS: FoqaThresholdParam[] = [
	// §US-015/US-016: departure/arrival splits of max_speed_below_10000 / max_taxi_speed above.
	{
		key: "max_speed_below_10000_departure",
		label: "Max speed below 10,000' — departure",
		phase: "departure",
		unit: "kt",
		weight: 0,
		informational: true,
	},
	{
		key: "max_speed_below_10000_arrival",
		label: "Max speed below 10,000' — arrival",
		phase: "arrival",
		unit: "kt",
		weight: 0,
		informational: true,
	},
	{
		key: "max_taxi_speed_departure",
		label: "Max taxi speed — departure",
		phase: "departure",
		unit: "kt",
		weight: 0,
		informational: true,
	},
	{
		key: "max_taxi_speed_arrival",
		label: "Max taxi speed — arrival",
		phase: "arrival",
		unit: "kt",
		weight: 0,
		informational: true,
	},
	{
		key: "takeoffRollDistanceFt",
		label: "Ground roll distance",
		phase: "departure",
		unit: "ft",
		weight: 0,
		informational: true,
	},
	{
		key: "takeoffRollPercentOfTora",
		label: "Ground roll, share of runway",
		phase: "departure",
		unit: "%",
		weight: 0,
		informational: true,
	},
	{
		key: "rotationIasKt",
		label: "Rotation airspeed",
		phase: "departure",
		unit: "kt",
		weight: 0,
		informational: true,
	},
	{
		key: "rotationRateDegPerSec",
		label: "Rotation rate",
		phase: "departure",
		unit: "deg/s",
		weight: 0,
		informational: true,
	},
	{
		key: "liftoffIasKt",
		label: "Liftoff airspeed",
		phase: "departure",
		unit: "kt",
		weight: 0,
		informational: true,
	},
	{
		key: "iasAt35FtKt",
		label: "Airspeed at 35 ft",
		phase: "departure",
		unit: "kt",
		weight: 0,
		informational: true,
	},
	{
		key: "pitchAt35FtDeg",
		label: "Pitch at 35 ft",
		phase: "departure",
		unit: "deg",
		weight: 0,
		informational: true,
	},
	{
		key: "distanceTo35Ft",
		label: "Distance to 35 ft",
		phase: "departure",
		unit: "ft",
		weight: 0,
		informational: true,
	},
	{
		key: "climbGradient35To1000FtPerNm",
		label: "Climb gradient, 35 ft to 1,000 ft",
		phase: "departure",
		unit: "ft/nm",
		weight: 0,
		informational: true,
	},
	{
		key: "gearUpAfeFt",
		label: "Gear selected up height",
		phase: "departure",
		unit: "ft",
		weight: 0,
		informational: true,
	},
	{
		key: "gearUpIasKt",
		label: "Gear selected up airspeed",
		phase: "departure",
		unit: "kt",
		weight: 0,
		informational: true,
	},
	{
		key: "flapsUpAfeFt",
		label: "Flaps selected up height",
		phase: "departure",
		unit: "ft",
		weight: 0,
		informational: true,
	},
	{
		key: "flapsUpIasKt",
		label: "Flaps selected up airspeed",
		phase: "departure",
		unit: "kt",
		weight: 0,
		informational: true,
	},
	{
		key: "autopilotEngagedAfeFt",
		label: "Autopilot engaged height",
		phase: "departure",
		unit: "ft",
		weight: 0,
		informational: true,
	},
	{
		key: "autopilotDisengagedAfeFt",
		label: "Autopilot off height",
		phase: "arrival",
		unit: "ft",
		weight: 0,
		informational: true,
	},
	{
		key: "finalAlignmentDistanceFromThresholdNm",
		label: "Aligned by",
		phase: "arrival",
		unit: "nm",
		weight: 0,
		informational: true,
	},
	{
		key: "finalAlignmentAfeFt",
		label: "Height at alignment",
		phase: "arrival",
		unit: "ft",
		weight: 0,
		informational: true,
	},
	{
		key: "touchdownTo70KtsFt",
		label: "Touchdown to 70 kt",
		phase: "arrival",
		unit: "ft",
		weight: 0,
		informational: true,
	},
	{
		key: "touchdownToTaxiSpeedFt",
		label: "Touchdown to taxi speed",
		phase: "arrival",
		unit: "ft",
		weight: 0,
		informational: true,
	},
	{
		key: "runwayUsedFt",
		label: "Runway used",
		phase: "arrival",
		unit: "ft",
		weight: 0,
		informational: true,
	},
	{
		key: "runwayRemainingAtTaxiSpeedFt",
		label: "Runway remaining at taxi speed",
		phase: "arrival",
		unit: "ft",
		weight: 0,
		informational: true,
	},
];

const toFlag = (p: FoqaThresholdParam): FoqaFlagSpec => {
	const s = p.safetyEvent ?? {};
	return {
		key: p.key,
		label: p.label,
		phase: p.phase,
		entity: s.entity ?? p.entity ?? "",
		appliesTo: p.appliesTo ?? "both",
		segment: s.segment ?? p.segment,
		belowAfe: s.belowAfe ?? p.belowAfe,
		durationSec: s.durationSec ?? p.durationSec,
		relativeTo: s.relativeTo,
		dir: s.dir,
		min: s.min,
		max: s.max,
		caution: s.caution,
		warning: s.warning,
	};
};

const stripGate = (p: FoqaThresholdParam): FoqaThresholdParam => {
	const clone = { ...p };
	delete clone.safetyEvent;
	return clone;
};

export const DEFAULT_FOQA_FLAGS: FoqaFlagConfig =
	[...SCORED_PARAMS, ...CJP_EXCEEDANCE_EVENTS].filter((p) => p.safetyEvent).map(toFlag);

export const DEFAULT_APPROACH_SCORECARD: FoqaThresholdConfig =
	[...SCORED_PARAMS, ...FOQA_WHOLE_FLIGHT_INFORMATIONAL_PARAMS].map(stripGate);

export const DEFAULT_FOQA_THRESHOLD_CONFIGS: Record<string, FoqaThresholdConfig> = {
	/** Universal fallthrough — scores any model without a specific row (see above). */
	"default": DEFAULT_APPROACH_SCORECARD,
};
